import Stack from '@mui/material/Stack'
import type { TFunction } from 'i18next'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import BidStatusChip from '@/features/bidding/components/BidStatusChip'
import { Status } from '@/features/bidding/constants'

type EnergyBidStatusChipsProps = {
  statusCounts: Partial<Record<Status, number>>
}

const ENERGY_STATUS_ORDER: Status[] = [Status.REJECTED, Status.ACCEPTED, Status.OFFERED, Status.DRAFT, Status.NO_BID]

const orderedEnergyStatuses = (statusCounts: Partial<Record<Status, number>>): Status[] => {
  const present = (Object.keys(statusCounts) as Status[]).filter((status) => (statusCounts[status] ?? 0) > 0)
  return [
    ...ENERGY_STATUS_ORDER.filter((status) => present.includes(status)),
    ...present.filter((status) => !ENERGY_STATUS_ORDER.includes(status)),
  ]
}

const getEnergyStatusLabel = (status: Status, t: TFunction<'translation'>): string =>
  status === Status.ACCEPTED ? t('bidding.status.ACKNOWLEDGED') : t(`bidding.status.${status}`)

function EnergyBidStatusChips({ statusCounts }: Readonly<EnergyBidStatusChipsProps>): ReactElement {
  const { t } = useTranslation()
  const statuses = orderedEnergyStatuses(statusCounts)

  return (
    <Stack sx={{ flexDirection: 'row', gap: 0.5, alignItems: 'center', height: '100%', width: 'fit-content' }}>
      {statuses.length === 0 ? (
        <BidStatusChip status={Status.NO_BID} />
      ) : (
        statuses.map((status) => (
          <BidStatusChip
            key={status}
            label={String(statusCounts[status])}
            status={status}
            title={getEnergyStatusLabel(status, t)}
          />
        ))
      )}
    </Stack>
  )
}

export default EnergyBidStatusChips
